import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { FeedbackEvent } from '@bonusly/common-feedback';
import {
	ArchiveOutline,
	Box,
	ChatAlt2Outline,
	ChatAltOutline,
	ChatOutline,
	ChevronRightOutline,
	ClappingHandsOutline,
	EmojiHappyOutline,
	HStack,
	MegaphoneOutline,
	PlusSquareSolid,
	rem,
	TargetOutline,
	Text,
	TrophyOutline,
} from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { Permission, ProtectedContent, useCurrentUser } from '@bonusly/who-am-i';

import { useMakeAnnouncementModal } from '../../../components/announcements/hooks';
import { Conceal } from '../../../components/display/conceal';
import { Reveal } from '../../../components/display/reveal';
import { GiveBoxEvent } from '../../../components/recognition/give-box/events';
import { useNominationsCount } from '../../../queries/nominations';
import { useAwardData } from '../../bonus-feed/features/recognition/components/Awards/hooks/useAwardData';
import { useAwardModal } from '../../bonus-feed/features/recognition/components/Awards/hooks/useAwardModal';
import { useCheckInModal } from '../../check-ins/hooks/use-checkin-modal';
import { useCreateGoalModal } from '../../growth/components/goals';
import { useNominationModal } from '../../nominations/hooks';
import { useCheckInsConfigured } from '../../team-management/hooks/use-check-ins-configured';
import { IconAndTextContent } from './icon-and-text-content';
import { UserNavAction } from './user-nav-action';
import { UserNavMenu } from './user-nav-menu';

type CreateNewMenuProps = {
	isExpanded: boolean;
};

export const CreateNewMenu: FC<CreateNewMenuProps> = ({ isExpanded }) => {
	const { t } = useTranslation('navigation');
	const dispatch = useDispatch();
	const user = useCurrentUser();

	const { onOpen: handleOpenCheckInModal } = useCheckInModal();
	const { onOpen: handleOpenGoalModal } = useCreateGoalModal();
	const { onOpen: handleAwardModal } = useAwardModal();
	const { onOpen: handleNominateModal } = useNominationModal();
	const { onOpen: handleOpenAnnouncementModal } = useMakeAnnouncementModal();

	const handleShareAdvice = () => {
		dispatch(FeedbackEvent.OpenUnsolicited);
	};

	const handleAskForAdvice = () => {
		dispatch(FeedbackEvent.OpenRequest);
	};

	const checkInsEnabled = useCheckInsConfigured();
	const { shouldDisplayBanner: hasAwardsToGive, isLoading: isLoadingAwards } = useAwardData();
	const { open: nominationsOpen, isLoading: isLoadingNominations } = useNominationsCount();

	if (isLoadingAwards || isLoadingNominations) {
		return null;
	}

	return (
		<Box mt={rem(8)}>
			<UserNavMenu buttonIcon={<PlusSquareSolid size="md" color="blue.500" />} buttonText={t('createNewButtonText')} isExpanded={isExpanded}>
				{/* GIVE RECOGNITION */}
				<ProtectedContent permissions={Permission.CanGiveRecognition}>
					<UserNavAction onClick={() => dispatch(GiveBoxEvent.Open)} data-testid="nav-action-recognition">
						<IconAndTextContent icon={<ClappingHandsOutline size="md" />} label={t('createNew.recognition')} isExpanded />
					</UserNavAction>
				</ProtectedContent>

				{/* CHECK IN */}
				<ProtectedContent permissions={Permission.CanAccessCheckIn}>
					<Reveal if={checkInsEnabled}>
						<UserNavAction onClick={() => handleOpenCheckInModal()} data-testid="nav-action-check-in">
							<IconAndTextContent icon={<EmojiHappyOutline size="md" />} label={t('createNew.checkIn')} isExpanded />
						</UserNavAction>
					</Reveal>
				</ProtectedContent>

				{/* ADVICE (FEEDBACK) */}
				<ProtectedContent permissions={Permission.CanAccessOneOnOnes}>
					{/* NOTE: If user is a manager, show user nav menu item for share advice and ask for advice */}
					<Reveal if={user.is_manager}>
						<UserNavMenu
							buttonIcon={<ChatAltOutline size="md" />}
							buttonContents={
								<HStack justifyContent="space-between" alignItems="center" width="100%">
									<Text fontSize={100} fontWeight="medium" whiteSpace="nowrap">
										{t('createNew.adviceRequest')}
									</Text>
									<ChevronRightOutline size="sm" />
								</HStack>
							}
							isExpanded
							isSubmenu
							placement="right-start"
							offset={[0, 0]}
						>
							<UserNavAction onClick={handleShareAdvice} data-testid="nav-action-share-advice">
								<IconAndTextContent icon={<ChatOutline size="md" />} label={t('createNew.shareAdvice')} isExpanded />
							</UserNavAction>
							<UserNavAction onClick={handleAskForAdvice} data-testid="nav-action-advice-request">
								<IconAndTextContent icon={<ChatAlt2Outline size="md" />} label={t('createNew.askForAdvice')} isExpanded />
							</UserNavAction>
						</UserNavMenu>
					</Reveal>
					<Conceal if={user.is_manager}>
						<UserNavAction onClick={handleAskForAdvice} data-testid="nav-action-advice-request">
							<IconAndTextContent icon={<ChatAltOutline size="md" />} label={t('createNew.adviceRequest')} isExpanded />
						</UserNavAction>
					</Conceal>
				</ProtectedContent>

				{/* ADD GOAL */}
				<ProtectedContent permissions={Permission.CanAccessGoals}>
					<UserNavAction onClick={() => handleOpenGoalModal()} data-testid="nav-action-goal">
						<IconAndTextContent icon={<TargetOutline size="md" />} label={t('createNew.goal')} isExpanded />
					</UserNavAction>
				</ProtectedContent>

				{/* AWARD */}
				<ProtectedContent permissions={Permission.CanAccessAwards}>
					<Reveal if={hasAwardsToGive}>
						<UserNavAction onClick={() => handleAwardModal()} data-testid="nav-action-award">
							<IconAndTextContent icon={<TrophyOutline size="md" />} label={t('createNew.award')} isExpanded />
						</UserNavAction>
					</Reveal>
				</ProtectedContent>

				{/* NOMINATE TEAMMATE */}
				<ProtectedContent permissions={Permission.CanAccessNominations}>
					<Reveal if={nominationsOpen > 0}>
						<UserNavAction onClick={() => handleNominateModal()} data-testid="nav-action-nomination">
							<IconAndTextContent icon={<ArchiveOutline size="md" />} label={t('createNew.nomination')} isExpanded />
						</UserNavAction>
					</Reveal>
				</ProtectedContent>

				{/* MAKE ANNOUNCEMENT */}
				<ProtectedContent permissions={Permission.CanAdministerAnnouncements}>
					<UserNavAction onClick={handleOpenAnnouncementModal} data-testid="nav-action-announcement">
						<IconAndTextContent icon={<MegaphoneOutline size="md" />} label={t('createNew.announcement')} isExpanded />
					</UserNavAction>
				</ProtectedContent>
			</UserNavMenu>
		</Box>
	);
};
